import { createOptionalColumnDefs } from "./OptionalColumns";
import { lookupIdToName, lookupIdToNameMappings } from "./Helpers";
import { CollisionMode } from "../components/SelectCollisionMode";
import _ from "lodash";

const getIgnoredAttributes = (distributionVersion) => {
  const ignoredAttributes = [
    "AssetId",
    "AssetTagId",
    "AlarmDigitalOutputChannel",
    "Alarm2DigitalOutputChannel",
    "CodeLegendId",
    "DisplayAsDetail",
    "GaugeTypeId",
    "PerformScalingOnServer",
    "Property",
    "ServerGain",
    "ServerOffset",
    "SparseData",
    "StrappingTableId",
    "UpdatedBy",
  ];
  if (parseFloat(distributionVersion) < 3.4) {
    // Old hubs don't have a UniversalDescriptor
    ignoredAttributes.push("UniversalDescriptor");
  }
  return ignoredAttributes;
};
const alarmLimitAttributes = ["AlarmLoLo", "AlarmLo", "AlarmHi", "AlarmHiHi"];
const notOverwrittenAttributes = [
  "AssetId",
  "AssetTagId",
  "LoBandNotificationPriorityId",
  "HiBandNotificationPriorityId",
  "CodeLegendId",
  "StrappingTableId",
  "IsDirty",
  "DisplayAsDetail",
  "SyncToHub",
  "Property",
  "SparseData",
];

const optionalColumnDefinitions = createOptionalColumnDefs([]);

/**
 * Compares a value in the user's current tag to a value in the tag from the hub.
 * The logic in this function is copied from asset-admin-tag.js' dialog.compareAttributes() function.
 * @param {User's current tag} userTag
 * @param {Corresponding tag from hub} tagFromHub
 * @param {Optional Column ID} columnId
 */
export function isValueDifferentForOptionalColumn(
  userTag,
  tagFromHub,
  columnId,
  distributionVersion
) {
  const lookupProperty = _.values(optionalColumnDefinitions).find(
    (optionalColumn) => optionalColumn.id === columnId
  ).accessorKey;
  return isValueDifferent(
    userTag,
    tagFromHub,
    lookupProperty,
    distributionVersion
  );
}

/**
 * Takes an array of tags to add and a map of tag identifier to tag pairs and builds a confirmation message.
 * @param {AssetTagPlusDTO[]} tagsToAdd
 * @param {Map<number, {userTag: AssetTagPlusDTO, tagFromHub: AssetTagPlusDTO}>} tagIdentifierMap
 * @param {number} distributionVersion
 * @returns {Array<{confirmationMessage: string, differences: string[]}>}
 */
export function buildConfirmationMessage(
  tagsToAdd,
  tagIdentifierMap,
  distributionVersion,
  lookupValues,
  collisionMode
) {
  let tagsToOverwrite = tagsToAdd.map((tag) =>
    tagIdentifierMap.get(tag.TagIdentifier)
  ); // Get the user and hub tags for the tag

  let confirmationMessages = tagsToOverwrite
    .map(({ userTag, tagFromHub }) => {
      if (!userTag || collisionMode === CollisionMode.Ignore) {
        return {
          confirmationMessage: `Tag ${tagFromHub.TagIdentifier} - ${tagFromHub.TagName} will be added to the user's tags.`,
          differences: [],
        };
      }

      if (userTag && collisionMode === CollisionMode.Skip) {
        return null;
      }

      let confirmationObject = {
        confirmationMessage: `Tag ${userTag.TagIdentifier} - ${userTag.TagName} will have values overwritten.`,
        differences: [],
      };
      if (userTag.TagName !== tagFromHub.TagName) {
        confirmationObject.confirmationMessage += ` All historical data for ${userTag.TagName} will remain associated with the tag.`;
      }
      const ignoredAttributes = getIgnoredAttributes(distributionVersion);
      Object.keys(tagFromHub).forEach((key) => {
        if (ignoredAttributes.includes(key)) {
          return;
        }
        if (!isValueDifferent(userTag, tagFromHub, key, distributionVersion)) {
          return;
        }
        const userTagValue = userTag[key];
        const tagFromHubValue = tagFromHub[key];
        if (!!lookupIdToNameMappings[key]) {
          const userDisplayValue = lookupIdToName(
            userTagValue,
            lookupIdToNameMappings[key],
            lookupValues
          );
          const tagFromHubDisplayValue = lookupIdToName(
            tagFromHubValue,
            lookupIdToNameMappings[key],
            lookupValues
          );
          confirmationObject.differences.push(
            ` ${lookupIdToNameMappings[key]} will be changed from [${userDisplayValue}] to [${tagFromHubDisplayValue}].`
          );
        } else {
          confirmationObject.differences.push(
            ` ${key} will be changed from [${userTagValue}] to [${tagFromHubValue}].`
          );
        }
      });

      return confirmationObject;
    })
    .filter((confirmationObject) => confirmationObject !== null);
  return confirmationMessages;
}

export function overwriteTag(userTag, tagFromHub, distributionVersion) {
  const newTag = { ...userTag };
  let preserveStrappingTableId = false;
  Object.keys(tagFromHub).forEach((key) => {
    if (
      getIgnoredAttributes(distributionVersion).includes(key) ||
      notOverwrittenAttributes.includes(key)
    ) {
      return;
    }

    const isTagLimitZero = alarmLimitAttributes.every(
      (attr) => tagFromHub[attr] === 0
    );
    if (isTagLimitZero && alarmLimitAttributes.includes(key)) {
      return;
    }
    if (
      key === "DisplayOrder" &&
      (tagFromHub[key] === null || tagFromHub[key] === 0)
    ) {
      return;
    }
    if (key === "UnitOfMeasure" && userTag["StrappingTableId"] > 0) {
      preserveStrappingTableId = true;
      return;
    }
    newTag[key] = tagFromHub[key];
  });
  if (preserveStrappingTableId) {
    newTag["StrappingTableId"] = userTag["StrappingTableId"];
  }
  return newTag;
}

function isValueDifferent(userTag, tagFromHub, key, distributionVersion) {
  if (
    getIgnoredAttributes(distributionVersion).includes(key) ||
    notOverwrittenAttributes.includes(key)
  ) {
    return false;
  }
  // Don't compare unit of measure for tag with strapping table
  if (key === "UnitOfMeasure" && userTag["StrappingTableId"] > 0) {
    return false;
  }
  // Ignore DisplayOrder if hub tag.DisplayOrder is null
  if (
    key === "DisplayOrder" &&
    (tagFromHub[key] === 0 || tagFromHub[key] === null)
  ) {
    return false;
  }

  const isTagLimitZero = alarmLimitAttributes.every(
    (attr) => tagFromHub[attr] === 0
  );
  if (isTagLimitZero && alarmLimitAttributes.includes(key)) {
    return false;
  }

  const userValue = userTag[key] === "" ? null : userTag[key];
  const tagFromHubValue = tagFromHub[key] === "" ? null : tagFromHub[key];
  return userValue !== tagFromHubValue;
}
